import { RENDER_DEFAULTS } from '../../../packages/avataros-three/src/applyRenderConfig.js'

// Builds the in-memory file map for an exported environment npm package.
// Pure function of its inputs — no fetching, no DOM — so it is unit-testable
// directly (see tests/unit/environmentPackage.test.mjs).
//
// input: {
//   id, name, version, splatFormat,
//   pose,                 // { px,py,pz,rx,ry,rz,scale }
//   splatBytes,           // Uint8Array
//   iblBytes,             // Uint8Array | null   — equirect IBL (PMREM source)
//   lightprobeBytes,      // Uint8Array | null   — diffuse SH probe-volume JSON
//   reflections,          // { manifestBytes: Uint8Array, captures: [{ i, bytes }] } | null
// }
// returns: { packageName, files: Array<{ name, data: Uint8Array }> }
export function buildEnvironmentPackage(input) {
  const enc = new TextEncoder()
  const packageName = `@avataros/environment-${input.id}`
  const splatPath = `assets/splat.${input.splatFormat}`

  // Lighting paths (each only present when the corresponding bake exists for
  // this splat). All three lighting datasets are produced by ENG-561 and keyed
  // by the same splatCacheKey — bundling them lets a consumer reproduce the
  // app's actual lighting (probe volume + reflections), not just the equirect.
  const iblPath = input.iblBytes ? 'assets/ibl.png' : null
  const lightprobesPath = input.lightprobeBytes ? 'assets/lighting/lightprobes.json' : null
  const reflections = input.reflections ?? null
  const reflectionsPath = reflections ? 'assets/lighting/reflections/manifest.json' : null

  // Assets-only package. The manifest is the machine-readable contract: it
  // tells a consumer (human or AI agent) exactly what files exist, the splat
  // format, the saved pose, and the render settings needed to reproduce the
  // AvatarOS authoring view. The package ships NO runtime code dependency —
  // consumers render it with their own (public) three, which carries both the
  // splat renderer and a loader for every format this can contain. That keeps
  // the package self-contained and installable with zero private-registry auth.
  const manifest = {
    schemaVersion: '1.0.0',
    kind: 'environment',
    name: input.name,
    id: input.id,
    files: {
      splat: splatPath,
      ...(iblPath ? { ibl: iblPath } : {}),
      ...(lightprobesPath ? { lightprobes: lightprobesPath } : {}),
      ...(reflectionsPath ? { reflections: reflectionsPath } : {}),
    },
    splatFormat: input.splatFormat,
    pose: normalizePose(input.pose),
    render: { ...RENDER_DEFAULTS },
  }

  const packageJson = {
    name: packageName,
    version: input.version,
    description: `AvatarOS exported environment "${input.name}" (assets only).`,
    type: 'module',
    main: 'index.js',
    types: 'index.d.ts',
    exports: { '.': './index.js', './manifest.json': './manifest.json' },
    files: ['index.js', 'index.d.ts', 'manifest.json', 'llms.txt', 'assets'],
  }

  // Data-only entry: exposes the manifest plus resolved asset URLs so a
  // programmatic consumer can fetch them. No imports → no dependencies.
  const indexJs = `export const manifest = ${JSON.stringify(manifest, null, 2)}

const baseUrl = new URL('./', import.meta.url)
const resolve = (rel) => (rel ? new URL(rel, baseUrl).href : null)

// Absolute URLs to the bundled assets, resolved relative to this module.
export const splatUrl = resolve(manifest.files.splat)
export const iblUrl = resolve(manifest.files.ibl)
export const lightprobesUrl = resolve(manifest.files.lightprobes)
export const reflectionsUrl = resolve(manifest.files.reflections)
`

  const indexDts = `export declare const manifest: {
  schemaVersion: string
  kind: 'environment'
  name: string
  id: string
  files: { splat: string; ibl?: string; lightprobes?: string; reflections?: string }
  splatFormat: string
  pose: { px: number; py: number; pz: number; rx: number; ry: number; rz: number; scale: number }
  render: Record<string, unknown>
}
export declare const splatUrl: string
export declare const iblUrl: string | null
export declare const lightprobesUrl: string | null
export declare const reflectionsUrl: string | null
`

  const lightingList = [
    iblPath ? '- assets/ibl.png                         — equirectangular IBL lightmap (PMREM source)' : null,
    lightprobesPath ? '- assets/lighting/lightprobes.json        — diffuse SH light-probe volume' : null,
    reflectionsPath ? '- assets/lighting/reflections/manifest.json + <i>.png — specular reflection captures' : null,
  ].filter(Boolean).join('\n')

  const llms = `# ${packageName}

AvatarOS exported Gaussian-splat environment — ASSETS ONLY.
This package contains a Gaussian splat plus baked lighting data and a
machine-readable manifest. It has NO runtime dependencies and no backend/API
dependency: install it and read the assets directly.

## Install
npm install ${packageName}

## What's inside
- assets/${splatPath.split('/').pop()}  — the Gaussian splat (${input.splatFormat})
${lightingList ? lightingList + '\n' : ''}- manifest.json         — the contract: files, pose, and render settings (below)
- index.js              — exports { manifest, splatUrl, iblUrl, lightprobesUrl, reflectionsUrl }

## How to use the assets
import { manifest, splatUrl, iblUrl, lightprobesUrl, reflectionsUrl } from '${packageName}'

Render with your own public deps:
- three.js r186 or newer. It ships both halves: \`GaussianSplat\` draws the splats,
  and \`SPZLoader\` / \`GaussianSplatPLYLoader\` / \`SPLATLoader\` / \`KSPLATLoader\`
  parse every format this package can contain. No third-party splat renderer.
  Note \`GaussianSplat\` is a NodeMaterial, so it needs \`WebGPURenderer\` — which
  falls back to its own WebGL2 backend where WebGPU is missing.

Steps for an exact match to the AvatarOS authoring view:
1. Load the splat from \`splatUrl\` with the loader for its format.
2. Place it using \`manifest.pose\` (px,py,pz / rx,ry,rz radians / scale).
3. Lighting (use whichever are present in manifest.files):
   - \`iblUrl\`: equirect texture → PMREMGenerator → scene.environment.
   - \`lightprobesUrl\`: diffuse SH probe volume; sample at the object's world
     position and drive a THREE.LightProbe (trilinear interpolation).
   - \`reflectionsUrl\`: reflection-capture manifest; the <i>.png captures sit
     beside it. Pick nearest captures by influence radius, PMREM to a specular
     envMap.
4. Apply \`manifest.render\`: toneMapping=${RENDER_DEFAULTS.toneMapping},
   toneMappingExposure=${RENDER_DEFAULTS.toneMappingExposure},
   outputColorSpace=${RENDER_DEFAULTS.outputColorSpace}.

All of this is declared in manifest.json (machine-readable) for agents.
`

  const readme = `# ${packageName}\n\nExported AvatarOS environment "${input.name}". See llms.txt for usage.\n`

  const files = [
    { name: 'package/package.json', data: enc.encode(JSON.stringify(packageJson, null, 2)) },
    { name: 'package/manifest.json', data: enc.encode(JSON.stringify(manifest, null, 2)) },
    { name: 'package/llms.txt', data: enc.encode(llms) },
    { name: 'package/README.md', data: enc.encode(readme) },
    { name: 'package/index.js', data: enc.encode(indexJs) },
    { name: 'package/index.d.ts', data: enc.encode(indexDts) },
    { name: `package/${splatPath}`, data: input.splatBytes },
  ]
  if (iblPath) files.push({ name: `package/${iblPath}`, data: input.iblBytes })
  if (lightprobesPath) files.push({ name: `package/${lightprobesPath}`, data: input.lightprobeBytes })
  if (reflectionsPath) {
    files.push({ name: `package/${reflectionsPath}`, data: reflections.manifestBytes })
    for (const cap of reflections.captures ?? []) {
      files.push({ name: `package/assets/lighting/reflections/${cap.i}.png`, data: cap.bytes })
    }
  }

  return { packageName, files }
}

function normalizePose(raw) {
  const p = (raw && typeof raw === 'object') ? raw : {}
  return {
    px: Number(p.px ?? 0), py: Number(p.py ?? 0), pz: Number(p.pz ?? 0),
    rx: Number(p.rx ?? 0), ry: Number(p.ry ?? 0), rz: Number(p.rz ?? 0),
    scale: Number(p.scale ?? 1),
  }
}
